// packages/main-app/src/utils/wallpaperPreference.ts

export type WallpaperSource = "preset" | "user" | "community" | "";
export type WallpaperMediaType = "image" | "video";

export const WALLPAPER_PREFERENCE_KEY = "eweadn-wallpaper-preference";

export interface WallpaperPreference {
  wallpaperEnabled: boolean;
  wallpaperUrl: string;
  wallpaperSource: WallpaperSource;
  wallpaperId: string | number | null;
  wallpaperMediaType: WallpaperMediaType;
  wallpaperBlur: number;
  wallpaperBrightness: number;
  wallpaperOpacity: number;
  wallpaperComponentBgTransparency: number;
}

const DEFAULT_PREFERENCE: WallpaperPreference = {
  wallpaperEnabled: false,
  wallpaperUrl: "",
  wallpaperSource: "",
  wallpaperId: null,
  wallpaperMediaType: "image",
  wallpaperBlur: 0,
  wallpaperBrightness: 1,
  wallpaperOpacity: 1,
  wallpaperComponentBgTransparency: 100,
};

const normalizeWallpaperMediaType = (value: unknown): WallpaperMediaType =>
  value === "video" ? "video" : "image";

/** 仅用 pathname 判断是否同一张图，忽略 CDN 域名与签名 query */
export const getWallpaperPathKey = (targetUrl: string): string => {
  if (!targetUrl) return "";

  const rawUrl = targetUrl.replace(/^url\(["']?/, "").replace(/["']?\)$/, "");

  try {
    return new URL(rawUrl, window.location.origin).pathname;
  } catch {
    return rawUrl.split("?")[0].split("#")[0];
  }
};

export const isSameWallpaperAsset = (a: string, b: string): boolean =>
  Boolean(a && b && getWallpaperPathKey(a) === getWallpaperPathKey(b));

export const readWallpaperPreference = (): WallpaperPreference => {
  try {
    const raw = localStorage.getItem(WALLPAPER_PREFERENCE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCE };

    const parsed = JSON.parse(raw) as Partial<WallpaperPreference>;
    const wallpaperUrl =
      typeof parsed.wallpaperUrl === "string" &&
      !parsed.wallpaperUrl.startsWith("data:")
        ? parsed.wallpaperUrl
        : "";

    return {
      wallpaperEnabled: Boolean(parsed.wallpaperEnabled && wallpaperUrl),
      wallpaperUrl,
      wallpaperSource:
        parsed.wallpaperSource === "preset" ||
        parsed.wallpaperSource === "user" ||
        parsed.wallpaperSource === "community"
          ? parsed.wallpaperSource
          : "",
      wallpaperId: parsed.wallpaperId === undefined ? null : parsed.wallpaperId,
      wallpaperMediaType: normalizeWallpaperMediaType(
        parsed.wallpaperMediaType
      ),
      wallpaperBlur:
        typeof parsed.wallpaperBlur === "number" ? parsed.wallpaperBlur : 0,
      wallpaperBrightness:
        typeof parsed.wallpaperBrightness === "number"
          ? parsed.wallpaperBrightness
          : 1,
      wallpaperOpacity:
        typeof parsed.wallpaperOpacity === "number"
          ? parsed.wallpaperOpacity
          : 1,
      wallpaperComponentBgTransparency:
        typeof parsed.wallpaperComponentBgTransparency === "number"
          ? Math.min(
              100,
              Math.max(0, Math.round(parsed.wallpaperComponentBgTransparency))
            )
          : 100,
    };
  } catch {
    return { ...DEFAULT_PREFERENCE };
  }
};

export const writeWallpaperPreference = (
  preference: WallpaperPreference
): void => {
  try {
    const rawUrl =
      typeof preference.wallpaperUrl === "string"
        ? preference.wallpaperUrl
        : "";
    const wallpaperUrl = rawUrl.startsWith("data:") ? "" : rawUrl;
    const next: WallpaperPreference = {
      wallpaperEnabled: Boolean(preference.wallpaperEnabled && wallpaperUrl),
      wallpaperUrl,
      wallpaperSource: wallpaperUrl ? preference.wallpaperSource : "",
      wallpaperId: wallpaperUrl ? preference.wallpaperId : null,
      wallpaperMediaType: wallpaperUrl
        ? preference.wallpaperMediaType
        : "image",
      wallpaperBlur: preference.wallpaperBlur,
      wallpaperBrightness: preference.wallpaperBrightness,
      wallpaperOpacity: preference.wallpaperOpacity,
      wallpaperComponentBgTransparency:
        preference.wallpaperComponentBgTransparency,
    };
    localStorage.setItem(WALLPAPER_PREFERENCE_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn("[wallpaper] 写入壁纸偏好失败", error);
  }
};

/** 从旧版 global-store 迁移一次壁纸偏好 */
export const migrateWallpaperPreferenceFromGlobalStore =
  (): WallpaperPreference => {
    const existing = readWallpaperPreference();
    if (existing.wallpaperUrl) return existing;

    try {
      const rawStore = localStorage.getItem("global-store");
      if (!rawStore) return existing;
      const store = JSON.parse(rawStore);
      const state = store?.state;
      if (!state || typeof state !== "object") return existing;

      const migrated: WallpaperPreference = {
        wallpaperEnabled: Boolean(state.wallpaperEnabled && state.wallpaperUrl),
        wallpaperUrl:
          typeof state.wallpaperUrl === "string" &&
          !state.wallpaperUrl.startsWith("data:")
            ? state.wallpaperUrl
            : "",
        wallpaperSource:
          state.wallpaperSource === "preset" ||
          state.wallpaperSource === "user" ||
          state.wallpaperSource === "community"
            ? state.wallpaperSource
            : "",
        wallpaperId: state.wallpaperId === undefined ? null : state.wallpaperId,
        wallpaperMediaType: normalizeWallpaperMediaType(
          state.wallpaperMediaType
        ),
        wallpaperBlur:
          typeof state.wallpaperBlur === "number" ? state.wallpaperBlur : 0,
        wallpaperBrightness:
          typeof state.wallpaperBrightness === "number"
            ? state.wallpaperBrightness
            : 1,
        wallpaperOpacity:
          typeof state.wallpaperOpacity === "number"
            ? state.wallpaperOpacity
            : 1,
        wallpaperComponentBgTransparency:
          typeof state.wallpaperComponentBgTransparency === "number"
            ? Math.min(
                100,
                Math.max(0, Math.round(state.wallpaperComponentBgTransparency))
              )
            : 100,
      };

      if (migrated.wallpaperUrl) {
        writeWallpaperPreference(migrated);
        return migrated;
      }
    } catch {
      /* ignore */
    }

    return existing;
  };
